import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useAuthStore } from "../../store/auth-store";
import { WelcomeStep } from "./steps/WelcomeStep";
import { CompanyStep } from "./steps/CompanyStep";
import { PackageStep, type SelectedPackage } from "./steps/PackageStep";
import { ModulesStep } from "./steps/ModulesStep";
import { UsersStep } from "./steps/UsersStep";
import { CompleteStep } from "./steps/CompleteStep";

const STEP_LABELS = ["Dobrodošlica", "Kompanija", "Paket", "Moduli", "Korisnici", "Završeno"];

export interface OnboardingData {
  selectedPackage: SelectedPackage | null;
}

/**
 * Onboarding wizard posle registracije firme (SignupPage -> /onboarding,
 * vidi App.tsx) - vidi razgovor "onboarding wizard, 6 koraka identično
 * mokapu, sa stvarnim podacima". Van AppShell-a (bez sidebar-a), ali i
 * dalje unutar RequireAuth - korisnik je već ulogovan (signup vraća JWT).
 *
 * Svaki step čita/piše STVARNE Finora podatke (GET/PATCH /company,
 * /company/subscription, /users), NEMA mock/hardkodovanih vrednosti - vidi
 * pojedinačne step komponente. Jedini izuzetak je "Moduli" korak, koji je
 * NAMERNO read-only prikaz (nema self-service toggle endpoint-a na
 * backend-u za pojedinačne module - EntitlementService modul-pristup
 * određuje iz IZABRANOG PAKETA, ne iz ručnih toggle-a).
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const companyName = useAuthStore((s) => s.companyName);
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({ selectedPackage: null });

  const goNext = () => setStep((s) => Math.min(s + 1, STEP_LABELS.length));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <div className="min-h-screen bg-app-bg">
      <header className="flex h-16 items-center justify-between border-b border-border bg-surface px-6">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-sm font-bold text-white">
            H
          </div>
          <span className="text-lg font-semibold text-slate-900">HAFiskal</span>
          <span className="ml-2 hidden text-sm text-slate-400 sm:inline">
            Vaš partner u jednostavnijem poslovanju
          </span>
        </div>
        {step === 1 ? (
          <button
            onClick={() => {
              logout();
              navigate("/prijava", { replace: true });
            }}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Već imate nalog? Odjavi se
          </button>
        ) : (
          <span className="text-sm text-slate-500">{companyName}</span>
        )}
      </header>

      <div className="mx-auto max-w-5xl px-6 py-8">
        <ol className="mb-8 flex items-center justify-between">
          {STEP_LABELS.map((label, idx) => {
            const num = idx + 1;
            const isDone = num < step;
            const isCurrent = num === step;
            return (
              <li key={label} className="flex flex-1 items-center last:flex-none">
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
                      isDone
                        ? "bg-brand text-white"
                        : isCurrent
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {isDone ? <Check className="h-4 w-4" /> : num}
                  </div>
                  <span
                    className={`hidden text-xs sm:block ${
                      isCurrent ? "font-medium text-slate-900" : "text-slate-400"
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {num < STEP_LABELS.length && (
                  <div className={`mx-2 h-px flex-1 ${isDone ? "bg-brand" : "bg-slate-200"}`} />
                )}
              </li>
            );
          })}
        </ol>

        {step === 1 && <WelcomeStep onNext={goNext} />}
        {step === 2 && <CompanyStep onNext={goNext} />}
        {step === 3 && (
          <PackageStep
            selected={data.selectedPackage}
            onSelected={(pkg) => setData((d) => ({ ...d, selectedPackage: pkg }))}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {step === 4 && <ModulesStep selectedPackage={data.selectedPackage} onNext={goNext} onBack={goBack} />}
        {step === 5 && <UsersStep selectedPackage={data.selectedPackage} onNext={goNext} onBack={goBack} />}
        {step === 6 && <CompleteStep selectedPackage={data.selectedPackage} />}
      </div>
    </div>
  );
}
